import axios from 'axios';
import { url } from '../../../env';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  Accept: 'application/json',
});

const jsonHeaders = () => ({
  ...authHeaders(),
  'Content-Type': 'application/json',
});

export const carteraApi = {
  // KPIs + aging del tablero.
  getDashboard: (params = {}, signal) =>
    axios.get(`${url}/cartera/dashboard`, { headers: authHeaders(), params, signal }),

  // Línea de tiempo: índice de meses navegables ({mes, label, tipo: vivo|real|simulado}).
  getSnapshots: (signal) =>
    axios.get(`${url}/cartera/snapshots`, { headers: authHeaders(), signal }),

  // Un mes de la línea de tiempo (YYYY-MM). data.tipo: 'vivo' (tablero normal),
  // 'real' (foto congelada del cierre) o 'simulado' (estructural reconstruido,
  // bloques de cobranza en null → mostrar aviso). data.dashboard = mismo shape
  // que getDashboard.
  getSnapshot: (periodo, signal) =>
    axios.get(`${url}/cartera/snapshots/${periodo}`, { headers: authHeaders(), signal }),

  // Lista de cartera (filtrable). estatus acepta uno o varios coma-separados;
  // la bandeja del día usa estatus=vencido,deteriorado.
  getCartera: (params = {}, signal) =>
    axios.get(`${url}/cartera`, { headers: authHeaders(), params, signal }),

  // Cobranza del mes: mensualidades del periodo (YYYY-MM) agrupadas por día de
  // pago. Devuelve { totales, grupos:[{dia,label,contratos,meta,cobrado,vencido,
  // pct,items}], proximos, vencidos }. Sin periodo → mes actual.
  getCobranzaMes: (periodo, signal) =>
    axios.get(`${url}/cartera/cobranza-mes`, { headers: authHeaders(), params: periodo ? { periodo } : {}, signal }),

  // Matriz de cobranza: contratos × meses. `hasta` (YYYY-MM) es el mes en curso
  // de la vista y `meses` (3|6|12) cuántos vencidos se muestran a su izquierda;
  // la ventana siempre cierra con el mes próximo. Devuelve
  // { columnas, filas:[{celdas:{'YYYY-MM':{esperado,pagado,pendiente,mora,estado}},
  // anteriores, comentario}], totales }. La mora viaja siempre aparte: el switch
  // de moratorios la suma o la quita sin volver a pedir nada.
  getMatriz: (params = {}, signal) =>
    axios.get(`${url}/cartera/matriz`, { headers: authHeaders(), params, signal }),

  // Datos bancarios oficiales y catálogo de mensajes. Fallback local si falla.
  getConfigMensajes: (signal) =>
    axios.get(`${url}/cartera/config-mensajes`, { headers: authHeaders(), signal }),

  // Conversaciones de WhatsApp. Incluye hilos reales, contratos pendientes de
  // iniciar y el catálogo de plantillas configurado por el backend.
  getConversaciones: (params = {}, signal) =>
    axios.get(`${url}/cartera/conversaciones`, { headers: authHeaders(), params, signal }),

  // `tab` (vencido|hoy|proximo) limita la vista previa a esa pestaña: el resto
  // de las filas llegan sin texto (preview_pendiente) y muchísimo más rápido.
  // `vista` elige qué se lista: 'hoy' (default del backend) es la bandeja del
  // día — solo lo que el motor puede mandar hoy, ya agrupado por persona —, y
  // 'todo' devuelve el calendario completo con sus pestañas.
  //
  // Cada fila agrega `autorizado` (un humano ya dio el OK y solo falta que se
  // cumpla la ventana de tiempo), `autorizado_at` y `sale_en_minutos` (null si
  // sale ya o si no aplica). Sin ellos el panel no puede distinguir lo que
  // falta aprobar de lo que ya está en camino — y un aviso invisible es
  // exactamente cómo se perdieron 44 en julio.
  getAvisos: (signal, tab = null, vista = null) =>
    axios.get(`${url}/cartera/avisos`, {
      headers: authHeaders(),
      signal,
      params: { ...(tab ? { tab } : {}), ...(vista ? { vista } : {}) },
    }),

  actualizarModoAvisos: (modo) =>
    axios.post(`${url}/cartera/avisos/modo`, { modo }, { headers: jsonHeaders() }),

  actualizarPausaAvisos: (pausado) =>
    axios.post(`${url}/cartera/avisos/pausa`, { pausado }, { headers: jsonHeaders() }),

  // Vista previa de UNA fila. El listado trae solo las primeras (tope del
  // backend); el resto llega con `preview_pendiente` y se piden con esto al
  // desplegar la tarjeta, en vez de dejarlas en "Cargando…" para siempre.
  previewAviso: (recordatorioId) =>
    axios.get(`${url}/cartera/avisos/${recordatorioId}/preview`, { headers: jsonHeaders() }),

  // 🔴 `forzarEspaciado` salta EXCLUSIVAMENTE la espera de 30 minutos entre
  // mensajes al mismo teléfono, que es comodidad. NUNCA el tope diario por
  // teléfono, el horario legal, el opt-out ni la promesa de pago: esos cuatro
  // son la diferencia entre cobrar y hostigar, y hay riesgo regulatorio
  // (CONDUSEF/REDECO) detrás. Quien lo mande igual: el backend los sigue
  // aplicando aunque llegue en true.
  //
  // Aprobar ya no significa "manda esto ya": lo que no alcanza a salir queda
  // en estado `autorizado` y el motor lo suelta al cumplirse la ventana, sin
  // volver a pedir permiso.
  enviarAviso: (recordatorioId, forzarEspaciado = false) =>
    axios.post(
      `${url}/cartera/avisos/${recordatorioId}/enviar`,
      { forzar_espaciado: Boolean(forzarEspaciado) },
      { headers: jsonHeaders() },
    ),

  enviarAvisosGrupo: (recordatorioIds, forzarEspaciado = false) =>
    axios.post(
      `${url}/cartera/avisos/enviar-grupo`,
      { recordatorio_ids: recordatorioIds, forzar_espaciado: Boolean(forzarEspaciado) },
      { headers: jsonHeaders() },
    ),

  excluirAviso: (recordatorioId) =>
    axios.post(`${url}/cartera/avisos/${recordatorioId}/excluir`, {}, { headers: jsonHeaders() }),

  // Página de mensajes más recientes en orden descendente. before carga
  // historia anterior sin mezclarla con el polling de la página actual.
  getConversacionMensajes: (id, params = {}, signal) =>
    axios.get(`${url}/cartera/conversaciones/${id}/mensajes`, { headers: authHeaders(), params, signal }),

  // Media entrante vive en S3 privado; se obtiene autenticada como Blob.
  getConversacionMedia: (conversacionId, mensajeId, signal) =>
    axios.get(`${url}/cartera/conversaciones/${conversacionId}/mensajes/${mensajeId}/media`, {
      headers: authHeaders(), signal, responseType: 'blob',
    }),

  // Texto libre (solo dentro de 24 h) o una de las plantillas manuales.
  enviarConversacionMensaje: (id, body, signal) =>
    axios.post(`${url}/cartera/conversaciones/${id}/mensajes`, body, { headers: jsonHeaders(), signal }),

  // Resetea no leídos y opcionalmente toma el hilo ({ tomar:true }). Tomar
  // también apaga la marca de "revisar" que deja el bot.
  marcarConversacionLeida: (id, body = {}, signal) =>
    axios.post(`${url}/cartera/conversaciones/${id}/leido`, body, { headers: jsonHeaders(), signal }),

  // Adjunta un documento (PDF) al hilo. Solo con la ventana de 24h abierta.
  // formData: archivo (File), credito_id?, caption?, request_id?. No fijar
  // Content-Type: axios pone el boundary del multipart.
  //
  // request_id (UUID) hace el intento idempotente: si axios reintenta o el
  // asesor da doble clic, el backend devuelve el mensaje que ya mandó en vez de
  // mandar el documento otra vez.
  enviarConversacionDocumento: (id, formData) =>
    axios.post(`${url}/cartera/conversaciones/${id}/documento`, formData, { headers: authHeaders() }),

  // Los datos con los que el navegador arma el PDF de la cotización del lead.
  // Autenticado y ligado a la conversación: el backend deriva el Budget de
  // meta.budget_id y NO devuelve el secret.
  //
  // Códigos que el botón distingue: 404 el lead no tiene cotización o la bandera
  // está apagada · 409 la recotización aceptada no está consolidada (o el Budget
  // no coincide) · 422 la cotización no tiene datos suficientes.
  getCotizacionPdfData: (id, signal) =>
    axios.get(`${url}/cartera/conversaciones/${id}/cotizacion-pdf-data`, { headers: authHeaders(), signal }),

  // Aviso de cotización actualizada: ¿cambió la cotización sin que el lead lo sepa?
  // GET → {pendiente, ahora:{plazo,pago_inicial,renta}|null, puede_avisar, motivo}.
  // POST manda el aviso por el chat; 409 {codigo: ventana_cerrada|sin_cambio} · 422 sin_datos.
  cambioCotizacion: (id, signal) =>
    axios.get(`${url}/cartera/conversaciones/${id}/cambio-cotizacion`, { headers: authHeaders(), signal }),

  avisarCambioCotizacion: (id) =>
    axios.post(`${url}/cartera/conversaciones/${id}/avisar-cambio-cotizacion`, {}, { headers: jsonHeaders() }),

  // Resolver la alerta de "[Ya pagué]". resultado: 'pagado' cierra la alerta;
  // 'no_pagado' la marca vencida y REANUDA los recordatorios automáticos.
  resolverGestion: (id, body) =>
    axios.post(`${url}/cartera/gestiones/${id}/resolver`, body, { headers: jsonHeaders() }),

  reverificarGestion: (id) =>
    axios.post(`${url}/cartera/gestiones/${id}/reverificar`, {}, { headers: jsonHeaders() }),

  getPlantillaParams: (creditoId, template, signal) =>
    axios.get(`${url}/cartera/${creditoId}/whatsapp/plantilla/${template}/params`, { headers: authHeaders(), signal }),

  // ── Mensaje rápido de la matriz de cobranza ────────────────────────────────
  // Todo lo que pinta el modal: plantillas elegibles con su vista previa YA
  // renderizada por el backend, la sugerida, el número destino real y
  // normalizado, el bloqueo (si aplica) y las advertencias.
  //
  // 🔴 La vista previa viene del backend a propósito. NO usar
  // vistaPreviaTemplate() de CarteraConversaciones: está hardcodeada, cubre 3
  // de las 6 plantillas y para las otras 3 pinta un texto FALSO.
  getMensajeRapido: (creditoId, signal) =>
    axios.get(`${url}/cartera/mensaje-rapido/${creditoId}`, { headers: authHeaders(), signal }),

  // Manda la plantilla YA (no encola). El backend revalida todos los cortes:
  // el modal pudo llevar media hora abierto y el horario legal, el estado del
  // hilo o los montos pueden haber cambiado.
  //
  // request_id (UUID) hace el intento idempotente: si axios reintenta o el
  // cobrador da doble clic, el backend devuelve lo que ya mandó en vez de
  // mandar un segundo WhatsApp.
  //
  // Códigos: 201 enviado · 200 este intento ya se había mandado · 409 se está
  // enviando en este momento · 422 bloqueado (con `motivo` para ramificar).
  enviarMensajeRapido: (creditoId, body, signal) =>
    axios.post(`${url}/cartera/mensaje-rapido/${creditoId}`, body, { headers: jsonHeaders(), signal }),

  // Inicia un hilo desde un contrato. telefono permite capturarlo inline si
  // falta; el backend lo guarda en budgets.phone (o meta para importados).
  enviarWhatsappPlantilla: (creditoId, body, signal) =>
    axios.post(`${url}/cartera/${creditoId}/whatsapp/plantilla`, body, { headers: jsonHeaders(), signal }),

  // Guarda el teléfono del contrato SIN enviar nada (va a budgets.phone).
  guardarTelefonoCredito: (creditoId, telefono, signal) =>
    axios.post(`${url}/cartera/${creditoId}/telefono`, { telefono }, { headers: jsonHeaders(), signal }),

  // Enviar el estado de cuenta por correo (envío REAL vía MyMail tipo 7).
  // formData: to, subject, content, cc[], attachments[] (el PDF). No fijar
  // Content-Type: axios pone el boundary del multipart.
  enviarEstadoCuentaEmail: (id, formData) =>
    axios.post(`${url}/cartera/${id}/estado-cuenta/email`, formData, { headers: authHeaders() }),

  // Alta manual de un contrato (botón "Nuevo contrato"). Nace LIGADO a su
  // persona (user_id del autocomplete, o user nuevo por nombre) vía Budget
  // oculto; para AF/CS genera su tabla de amortización. Devuelve el crédito.
  crearContrato: (body) =>
    axios.post(`${url}/cartera`, body, { headers: jsonHeaders() }),

  // Autocomplete de clientes (mismo endpoint que usa Vencimientos).
  buscarClientes: (q, signal) =>
    axios.get(`${url}/seguros-dashboard/clientes/buscar`, { headers: authHeaders(), params: { q }, signal }),

  // Detalle de un crédito (estado de cuenta + pagos).
  getCredito: (id, signal) =>
    axios.get(`${url}/cartera/${id}`, { headers: authHeaders(), signal }),

  // Registrar un pago sobre un crédito.
  registrarPago: (id, body) =>
    axios.post(`${url}/cartera/${id}/pagos`, body, { headers: jsonHeaders() }),

  // Marcar un crédito "en revisión" como revisado (limpia bandera + recalcula).
  marcarRevisado: (id) =>
    axios.post(`${url}/cartera/${id}/revisar`, {}, { headers: jsonHeaders() }),

  // Editar la tasa anual (% anual). AP: instantáneo (solo display/analítica).
  // AF/CS: el back responde 422 (requiere regenerar la amortización).
  editarTasa: (id, tasa_interes_anual) =>
    axios.post(`${url}/cartera/${id}/tasa`, { tasa_interes_anual }, { headers: jsonHeaders() }),

  // Vista previa del abono a capital (no escribe): reparto deudas/capital,
  // saldo antes→después, cuotas, interés ahorrado y comisión.
  abonoCapitalPreview: (id, body) =>
    axios.post(`${url}/cartera/${id}/abono-capital/preview`, body, { headers: jsonHeaders() }),

  // Aplicar abono a capital: baja el saldo y regenera la tabla pendiente con la
  // misma cuota (el contrato termina antes). Puede crear 2 pagos ligados (mixto).
  abonoCapital: (id, body) =>
    axios.post(`${url}/cartera/${id}/abono-capital`, body, { headers: jsonHeaders() }),

  // Desglose de liquidación anticipada (AF/CS: saldo + mora + servicios + comisión;
  // AP: capital referencial + rentas vencidas + mora). params: { condonar_mora: 1 }.
  liquidacionPreview: (id, signal, params) =>
    axios.get(`${url}/cartera/${id}/liquidacion`, { headers: authHeaders(), signal, params }),

  // Liquidar el crédito: cierra las cuotas pendientes y lo deja en estatus liquidado.
  liquidar: (id, body) =>
    axios.post(`${url}/cartera/${id}/liquidar`, body, { headers: jsonHeaders() }),

  // ── Liquidar por recuperación de auto ──────────────────────────────────────
  // body: { fecha_entrega:'YYYY-MM-DD', valor_auto?:number|null, motivo, notas?,
  // forma:'entrega'|'juicio' }. Preview NO escribe y devuelve además deuda,
  // faltante, tiene_aval, aval_nombre y resultado_sugerido; recuperar suma
  // resultado_buro:{titular, aval?, monto_pendiente?} (cierra el crédito sin
  // registrar pago: condona rentas/mora pendientes); revertir body: { motivo }.
  recuperacionPreview: (id, body) =>
    axios.post(`${url}/cartera/${id}/recuperacion/preview`, body, { headers: jsonHeaders() }),
  recuperar: (id, body) =>
    axios.post(`${url}/cartera/${id}/recuperacion`, body, { headers: jsonHeaders() }),
  revertirRecuperacion: (id, body) =>
    axios.post(`${url}/cartera/${id}/recuperacion/revertir`, body, { headers: jsonHeaders() }),
  // Resultado para Buró de una recuperación ya registrada (no vuelve a tocar la
  // cartera). Ambas responden { status, data: { cierre } }.
  // PATCH body: { forma?, valor_auto?:number|null, resultado_buro? }.
  recuperacionBuro: (id, body) =>
    axios.patch(`${url}/cartera/${id}/recuperacion/buro`, body, { headers: jsonHeaders() }),
  // POST body: { persona:'titular'|'aval', pagado:bool, fecha_pago?:'YYYY-MM-DD' }.
  recuperacionBuroPago: (id, body) =>
    axios.post(`${url}/cartera/${id}/recuperacion/buro/pago`, body, { headers: jsonHeaders() }),

  // ── Deshacer / corregir un pago ya registrado ──────────────────────────────
  // Todas usan el scoped binding {credito}/pagos/{pago}: si el pago no es de ese
  // crédito el back responde 404.

  // Vista previa de la reversión (NO escribe). Devuelve el veredicto del gate
  // ({puede, motivo}) más el efecto: filas que vuelven a su estado anterior
  // (de → a), saldo antes→después, mora (que NO se restaura: se recalcula a hoy)
  // y la condonación que se revertiría. El front pinta esto tal cual — las
  // reglas (LIFO, mes cerrado, pago importado…) viven SOLO en el backend.
  reversionPreview: (creditoId, pagoId, signal) =>
    axios.get(`${url}/cartera/${creditoId}/pagos/${pagoId}/reversion`, { headers: authHeaders(), signal }),

  // Deshacer el pago: restaura las filas desde pago.meta.reverso, devuelve la
  // condonación y deja el recibo en estatus 'cancelado' (NO se borra: sigue
  // visible con su folio quemado). body: { motivo } — obligatorio, se guarda en
  // meta.reversion junto con quién y cuándo.
  revertirPago: (creditoId, pagoId, body) =>
    axios.post(`${url}/cartera/${creditoId}/pagos/${pagoId}/revertir`, body, { headers: jsonHeaders() }),

  // Corregir el pago CONSERVANDO el folio (una sola fila en `pagos`).
  // body: { monto?, fecha?, canal?, notas?, motivo }. Solo canal/notas → update
  // directo sin recalcular; si cambia monto o fecha el back restaura las filas y
  // vuelve a aplicar el pago. El historial se acumula en meta.correcciones[].
  corregirPago: (creditoId, pagoId, body) =>
    axios.patch(`${url}/cartera/${creditoId}/pagos/${pagoId}`, body, { headers: jsonHeaders() }),

  // Condonar / descondonar la mora ligada a un pago. NO toca cuotas ni saldo.
  // body: { accion: 'aplicar' | 'quitar', motivo }. 'quitar' devuelve la mora al
  // crédito (vuelve a correr a hoy); 'aplicar' la perdona y la liga a este pago.
  condonacionPago: (creditoId, pagoId, body) =>
    axios.post(`${url}/cartera/${creditoId}/pagos/${pagoId}/condonacion`, body, { headers: jsonHeaders() }),

  // ── Bot de ventas (Fase 2, §8) ─────────────────────────────────────────────
  // Estas rutas viven fuera del prefijo /cartera: son /bot-ventas y las protege
  // check.permission SIN parámetro (solo admin PEI). Apagar el bot o devolver un
  // hilo al bot no es algo que deba poder hacer un vendedor.

  // Estado del switch global. Devuelve { config:{ activo, ... }, entorno:{
  // habilitado_efectivo, ... }, ... }. 🔴 `activo` es el switch que el panel
  // pinta; `habilitado_efectivo` dice si el bot puede contestar de verdad (el
  // env lo puede tener apagado aunque el switch diga que sí), y por eso la banda
  // de aviso se decide con el estado real, no solo con el switch.
  getBotVentasConfig: (signal) =>
    axios.get(`${url}/bot-ventas/config`, { headers: authHeaders(), signal }),

  // Enciende/apaga el bot para TODOS los leads (bot_ventas_configs.activo).
  // 🔴 Solo toca `activo`: NO escribe conversacion.modo de ningún hilo, así que
  // cobranza no se entera. Devuelve el MISMO shape que getBotVentasConfig, y el
  // panel se pinta con lo que respondió el backend, nunca con la posición
  // optimista del switch (§5.2: un switch no puede quedar mintiendo).
  actualizarBotVentasActivo: (activo) =>
    axios.post(`${url}/bot-ventas/config`, { activo: Boolean(activo) }, { headers: jsonHeaders() }),

  // A quién le llegan los avisos del bot (bot_ventas_configs.encargado_*). Manda
  // sobre BOT_VENTAS_AVISO_TEL / _EMAIL_TO del env. Mismo shape de respuesta.
  actualizarBotVentasEncargado: ({ encargado_user_id, encargado_telefono, encargado_email }) =>
    axios.post(`${url}/bot-ventas/config`, { encargado_user_id, encargado_telefono, encargado_email }, { headers: jsonHeaders() }),

  // Teléfonos de prueba (bot_ventas_configs.allowlist). Con números, el bot SOLO
  // les contesta a esos; vacía = a todos. Mismo shape de respuesta que getBotVentasConfig.
  actualizarBotVentasAllowlist: (allowlist) =>
    axios.post(`${url}/bot-ventas/config`, { allowlist }, { headers: jsonHeaders() }),

  // Plantillas de WhatsApp del bot con lo que Meta tiene dado de alta.
  // Devuelve { plantillas: [{ clave, nombre, encontrada, parametros, titulo, para,
  // cuando, estado?, categoria?, idioma?, encabezado?, cuerpo?, pie?, botones? }], error }.
  getBotVentasPlantillas: (signal) =>
    axios.get(`${url}/bot-ventas/plantillas`, { headers: authHeaders(), signal }),

  // Switches del panel "Ajustes del bot" (bot_ventas_configs.switches). Devuelve
  // { switches: [{ clave, valor, servidor, en_panel }] }. La base manda sobre el
  // .env y aplica en ≤ 60 s en la Lambda y en el EC2.
  getBotVentasSwitches: (signal) =>
    axios.get(`${url}/bot-ventas/switches`, { headers: authHeaders(), signal }),

  // `valor: null` = regresar al valor del servidor. Misma respuesta que el GET.
  guardarBotVentasSwitch: (clave, valor) =>
    axios.post(`${url}/bot-ventas/switches`, { clave, valor }, { headers: jsonHeaders() }),

  // Recordatorio por silencio (bot_ventas_configs.rescate_activo).
  actualizarBotVentasRescate: (activo) =>
    axios.post(`${url}/bot-ventas/config`, { rescate_activo: Boolean(activo) }, { headers: jsonHeaders() }),

  // Devuelve UN hilo pausado al bot (§4.3): limpia la pausa y pone modo=bot,
  // conservando cotización, budget_id y bitácora. 🔴 El backend RE-VALIDA que el
  // hilo sea cotización y responde 422 si no lo es: es la frontera con cobranza,
  // que comparte la columna `modo`. Devuelve { message, pausado }.
  reactivarBotVentas: (conversacionId) =>
    axios.post(`${url}/bot-ventas/conversaciones/${conversacionId}/reactivar`, {}, { headers: jsonHeaders() }),
};
